import { Button } from '@/components/ui/button';
import { toast } from '@/components/toast/toast';
import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader2Icon } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import routeConfig from '@/config/routeConfig';
import { Input } from '@/components/ui/input';
import styles from './sign-requested-document-modal.module.css';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { Certificate } from '@/entities/certificate';
import { getUserCertificates } from '@/use-cases/get-user-signatures';
import { SignRequest } from '@/entities/sign-request';
import { Label } from '@/components/ui/label';
import { PdfViewer, PdfViewerOverlayContext } from '@/components/pdf-viewer';
import { AnnotationPreview } from '../annotation-preview/annotation-preview';
import { Annotation } from '@/pages/upload-document/components/annotation/annotation';
import { normalizeStoredAnnotation } from '@/pages/upload-document/components/annotation/annotation-geometry';
import { getUserName } from '@/utils/get-user-name';
import { useAuthContext } from '@/components/auth-context/auth-context';
import { getSigningRequestDocument } from '@/use-cases/get-signing-request-document';
import { buildVisibleSignatureOptionsForAnnotation } from '@/pages/upload-document/util/build-visible-signature-options';
import { PDFDocument } from 'pdf-lib';
import { PinSignInput } from '@/components/pin-sign-input/pin-sign-input';
import { uploadDocument, UploadedDocumentData } from '@/use-cases/upload-document';
import notifyHB from '@/config/notifyHB';
import {
	signUploadedDocument,
	VisibleSignatureOptions,
} from '@/use-cases/sign-uploaded-document';
import { getSignedDocumentDownloadUrl } from '@/use-cases/get-signed-document-download-url';
import { downloadFromUrl } from '@/utils/download-file';
import { isHttpError } from '@/http';
import { signingRequestDocumentUpload } from '@/use-cases/signing-request-document-upload';

type SignRequestDocumentModalProps = {
	selectedSigningRequest: SignRequest | null;
	onClose: () => void;
};

export const SignRequestedDocumentModal = (
	props: SignRequestDocumentModalProps,
) => {
	const history = useHistory();
	const [isSigning, setIsSigning] = useState(false);
	const [isFetchingCertificates, setIsFetchingCertificates] = useState(false);
	const [isFetchingDocument, setIsFetchingDocument] = useState(false);
	const [certificates, setCertificates] = useState<Certificate[]>([]);
	const [selectedCertificate, setSelectedCertificate] = useState<
		Certificate['certificate_id'] | null
	>(null);
	const [otp, setOtp] = useState('');
	const [isPreparingDocument, setIsPreparingDocument] = useState(false);
	// Only the write side is used: signing addresses the document by
	// props.selectedSigningRequest.DocUlid, not by the upload result.
	const [, setUploadedDocumentData] =
		useState<UploadedDocumentData | null>(null);
	const [documentToSign, setDocumentToSign] = useState<File | null>(null);
	const [areFieldsSaved, setAreFieldsSaved] = useState(false);
	const savedFields = useRef<HTMLFormElement>(null);

	useEffect(() => {
		if (props.selectedSigningRequest) {
			setIsFetchingCertificates(true);
			setIsFetchingDocument(true);
			getSigningRequestDocument(
				props.selectedSigningRequest.RequestedBy,
				props.selectedSigningRequest.DocUlid,
				props.selectedSigningRequest.DocName,
			)
				.then(setDocumentToSign)
				.catch((error) =>
					notifyHB(
						'signing-requests',
						'SignRequestedDocumentModal',
						`Fail to get signing request document PDF with ${error}`,
					),
				)
				.finally(() => setIsFetchingDocument(false));
			getUserCertificates()
				.then((serverCertificates) => {
					if (serverCertificates) {
						setCertificates(
							serverCertificates.filter(
								(certificate) =>
									certificate.status === 'Active',
							),
						);
					}
				})
				.catch((error) => {
					toast({ mode: 'destructive', description: 'No fue posible subir el documento. Intenta de nuevo más tarde' });
					console.debug(error);
				})
				.finally(() => {
					setIsFetchingCertificates(false);
				});
		} else {
			setSelectedCertificate(null);
		}
	}, [props.selectedSigningRequest]);

	useEffect(() => {
		if (!selectedCertificate) {
			setOtp('');
			setUploadedDocumentData(null);
		}
	}, [selectedCertificate]);

	const handleSign = async () => {
		if (
			!props.selectedSigningRequest ||
			!documentToSign ||
			!selectedCertificate ||
			!otp
		) {
			return;
		}

		setIsSigning(true);
		try {
			const manuscriptComponents =
				props.selectedSigningRequest.Components.filter(
					(component) => component.type === 'manuscript',
				);

			let visibleSignatureOptions: VisibleSignatureOptions[] | undefined;
			if (manuscriptComponents.length > 0) {
				const pdfBytes = await documentToSign.arrayBuffer();
				const pdfDoc = await PDFDocument.load(pdfBytes);
				const currentUserName = getUserName(userProfile) ?? 'XXXXX';

				visibleSignatureOptions = manuscriptComponents.map(
					(component) =>
						buildVisibleSignatureOptionsForAnnotation(
							normalizeStoredAnnotation(component),
							pdfDoc,
							currentUserName,
						),
				);
			}

			const signDocumentReponse = await signUploadedDocument(
				props.selectedSigningRequest.DocUlid,
				props.selectedSigningRequest.DocName,
				selectedCertificate,
				otp,
				undefined,
				'pin',
				visibleSignatureOptions,
			);

			const signedDocumentDownloadUrl =
				await getSignedDocumentDownloadUrl(
					signDocumentReponse.signedPath,
				);

			if (signedDocumentDownloadUrl) {
				await downloadFromUrl(signedDocumentDownloadUrl, props.selectedSigningRequest.DocName);
			} else {
				notifyHB(
					'sign-document',
					'SignRequestedDocumentModal',
					'Failed to sign document. SignedDocumentDownloadUrl is not defined',
				);
			}

			props.onClose();
		} catch (error) {
			console.debug(error);
			if (isHttpError(error) && error.status === 400) {
				toast({ mode: 'caution', description: 'El PIN ingresado no es válido' });
			} else {
				notifyHB('sign-document', 'SignRequestedDocumentModal', error);
				toast({ mode: 'destructive', description: 'No es posible firmar. Intenta de nuevo más tarde' });
			}
		} finally {
			setIsSigning(false);
		}
	};

	const { userProfile } = useAuthContext();
	const userName = getUserName(userProfile);
	const userInitials = userName
		?.split(' ')
		.map((name) => name.trim()[0])
		.join('');

	const handleSaveFields: React.FormEventHandler<HTMLFormElement> = (
		event,
	) => {
		event.preventDefault();
		setAreFieldsSaved(true);
	};

	const handleCreateSignature = () => {
		if (props.selectedSigningRequest) {
			localStorage.setItem(
				'pending-signing-request-doc-ulid',
				props.selectedSigningRequest.DocUlid,
			);
		}
		history.push(routeConfig.requestSignature);
	};

	const handleSelectCertificate = async (
		newSelectedCertificate: Certificate['certificate_id'],
	) => {
		if (
			props.selectedSigningRequest &&
			savedFields.current &&
			documentToSign
		) {
			setSelectedCertificate(newSelectedCertificate);
			setIsPreparingDocument(true);
			try {
				// Upload documentToSign as-is - do NOT bake annotations into it client-side.
				// documentToSign may already carry a previous signer's cryptographic PAdES
				// signature (in a multi-signer chain); pdf-lib's save() always does a full,
				// non-incremental rewrite of the PDF object graph, which would invalidate
				// that prior signature's byte range. Visible manuscript stamps are instead
				// baked in cryptographically-safe fashion server-side (see
				// visible_signature_options / handleSign below); other annotation types
				// (text/date/initials/full name) aren't yet baked into the file in this flow.
				const uploadedDocumentInfo = await signingRequestDocumentUpload(
					props.selectedSigningRequest.RequestedBy,
					props.selectedSigningRequest.DocUlid,
					documentToSign,
				);
				setUploadedDocumentData(uploadedDocumentInfo);
			} catch (error) {
				toast({
					mode: 'destructive',
					description:
						'No fue posible preparar el documento para firmar. Intenta de nuevo',
				});
				notifyHB(
					'signing-requests',
					'SignRequestedDocumentModal',
					`Fail to prepare document for signing with ${error}`,
				);
			} finally {
				setIsPreparingDocument(false);
			}
		}
	};


	const componentsByPage = useMemo(() => {
		const byPage = new Map<
			number,
			{ annotation: Annotation; index: number }[]
		>();
		props.selectedSigningRequest?.Components.forEach((component, index) => {
			const annotation = normalizeStoredAnnotation(component);
			byPage.set(annotation.page, [
				...(byPage.get(annotation.page) ?? []),
				{ annotation, index },
			]);
		});
		return byPage;
	}, [props.selectedSigningRequest]);

	const renderPageOverlay = useCallback(
		({ pageIndex, size }: PdfViewerOverlayContext) =>
			componentsByPage
				.get(pageIndex)
				?.map(({ annotation, index }) => (
					<AnnotationPreview
						key={annotation.id}
						annotation={annotation}
						index={index}
						pageSize={size}
					/>
				)),
		[componentsByPage],
	);

	const handleDocumentError = useCallback(
		(error: unknown) =>
			notifyHB(
				'signing-requests',
				'SignRequestedDocumentModal',
				`Fail to render signing request document PDF with ${error}`,
			),
		[],
	);

	const documentHasFillableFields = useMemo<boolean>(() => {
		return (
			props.selectedSigningRequest?.Components.reduce(
				(accumulator, component) => {
					return (
						['text', 'date'].includes(component.type) || accumulator
					);
				},
				false,
			) ?? false
		);
	}, [props.selectedSigningRequest]);

	return (
		<AlertDialog
			open={props.selectedSigningRequest !== null}
			onOpenChange={(open) => (open ? {} : props.onClose())}
		>
			<AlertDialogContent
				className={styles['sign-requested-document-modal']}
			>
				<AlertDialogTitle className={styles['title']}>
					{props.selectedSigningRequest?.DocName}
				</AlertDialogTitle>
				<div className={styles['document-fields']}>
					<div
						className={styles['document-preview']}
					>
						{isFetchingDocument ? (
							<div
								style={{
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center',
									height: 100,
								}}
							>
								<Loader2Icon className="animate-spin" />
							</div>
						) : (
							<PdfViewer
								className={styles['document']}
								file={documentToSign}
								renderPageOverlay={renderPageOverlay}
								onError={handleDocumentError}
								errorMessage="No pudimos obtener el documento"
							/>
						)}
					</div>
					<div className={styles['sidebar-column']}>
					<form
						ref={savedFields}
						onSubmit={handleSaveFields}
						className={styles['fields-list']}
					>
						{props.selectedSigningRequest?.Components.map(
							(annotation, index) => {
								return (
									<div
										className={styles.field}
										key={annotation.id}
									>
										<Label>Anotación {index + 1}</Label>
										{['date', 'text'].includes(
											annotation.type,
										) && (
												<Input
													id={annotation.id}
													name={annotation.id}
													disabled={!documentToSign}
													type={annotation.type}
												/>
											)}
										{annotation.type === 'manuscript' && (
											<div
												className={
													styles['field-signature']
												}
											></div>
										)}
										{annotation.type === 'initials' && (
											<div
												className={
													styles['field-initials']
												}
											>
												{userInitials}
											</div>
										)}
										{annotation.type === 'full_name' && (
											<div
												className={styles['field-name']}
											>
												{userName}
											</div>
										)}
									</div>
								);
							},
						)}
						{documentHasFillableFields && (
							<Button
								size="sm"
								disabled={areFieldsSaved}
								type="submit"
							>
								Guardar
							</Button>
						)}
					</form>
				<div>
					{(!documentHasFillableFields || areFieldsSaved) &&
					!isFetchingCertificates &&
					certificates.length === 0 ? (
						<div className={styles['otp-accordion']}>
							<div />
							<div className={styles['input-group']}>
								<p>
									Aún no tienes una firma digital activa.
									Crea una para poder firmar este documento.
								</p>
								<Button onClick={handleCreateSignature}>
									Crear mi firma
								</Button>
							</div>
						</div>
					) : null}
					{(!documentHasFillableFields || areFieldsSaved) &&
					(isFetchingCertificates || certificates.length > 0) ? (
						<div className={styles['otp-accordion']}>
							<div />
							<div className={styles['input-group']}>
								¿Qué certificado quieres usar?
								<Select
									value={selectedCertificate ?? undefined}
									onValueChange={handleSelectCertificate}
									disabled={
										isFetchingCertificates ||
										isPreparingDocument ||
										!documentToSign
									}
								>
									<SelectTrigger>
										<SelectValue placeholder="Selecciona un certificado para firmar" />
										{isFetchingCertificates && (
											<Loader2Icon className="animate-spin" />
										)}
									</SelectTrigger>
									<SelectContent>
										{certificates.map((certificate) => (
											<SelectItem
												key={certificate.certificate_id}
												value={
													certificate.certificate_id
												}
											>
												{certificate.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						</div>
					) : null}
				</div>
				<div className={styles['otp']}>
					{selectedCertificate !== null && (
						<div className={styles['otp-accordion']}>
								<PinSignInput
									value={otp}
									setValue={setOtp}
									disabled={isSigning}
									autoFocus
								/>
						</div>
					)}
				</div>
				</div>
				</div>
				<AlertDialogFooter>
					<AlertDialogCancel asChild>
						<Button
							variant="secondary"
							disabled={isSigning}
							onClick={props.onClose}
						>
							Cancelar
						</Button>
					</AlertDialogCancel>
					<Button
						variant="default"
						onClick={handleSign}
						disabled={
							!selectedCertificate ||
							!otp ||
							isSigning ||
							!documentToSign
						}
					>
						Firmar
						{isSigning && <Loader2Icon className="animate-spin" />}
					</Button>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
};
